import { AllStak, AllStakProvider } from '@allstak/react';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from '@/lib/queryClient';
import { router } from '@/router';
import apiClient from '@/lib/apiClient';
import '@/i18n';
import { useLanguage } from '@/shared/hooks/useLanguage';
import './index.css';

function Root() {
  const { direction } = useLanguage();

  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
  }, [direction]);

  return (
    <>
      <RouterProvider router={router} />
      <Toaster
        position={direction === 'rtl' ? 'top-left' : 'top-right'}
        containerClassName="ds-toast-container"
        toastOptions={{
          style: {
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--grey-200)',
            fontSize: 14,
          },
        }}
      />
    </>
  );
}

function AllStakUserBootstrap() {
  useEffect(() => {
    if (window.location.pathname === '/login') return;
    let cancelled = false;
    apiClient
      .get('/admin/authz/me')
      .then((res) => {
        if (cancelled) return;
        const admin = res?.data?.admin;
        if (!admin?.id) return;
        AllStak.setUser({
          id: String(admin.id),
          ...(admin.email ? { email: admin.email } : {}),
        });
      })
      .catch(() => {
        // Not signed in or endpoint unreachable — leave user unset.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return null;
}

function AllStakRoot({ children }: { children: any }) {
  const apiKey = import.meta.env.VITE_ALLSTAK_API_KEY;
  if (!apiKey) return <>{children}</>;
  return (
    <AllStakProvider
      apiKey={apiKey}
      host={import.meta.env.VITE_ALLSTAK_HOST}
      environment={import.meta.env.VITE_ALLSTAK_ENVIRONMENT ?? 'production'}
      release={import.meta.env.VITE_ALLSTAK_RELEASE}
      dist={import.meta.env.VITE_ALLSTAK_DIST ?? 'web'}
      captureScreenshotOnError={import.meta.env.VITE_ALLSTAK_CAPTURE_SCREENSHOTS === 'true'}
      screenshotRedaction={(import.meta.env.VITE_ALLSTAK_SCREENSHOT_REDACTION as 'strict' | 'balanced' | 'custom') ?? 'strict'}
      screenshotMaskStyle={(import.meta.env.VITE_ALLSTAK_SCREENSHOT_MASK_STYLE as 'solid' | 'blur') ?? 'solid'}
      screenshotSampleRate={Number(import.meta.env.VITE_ALLSTAK_SCREENSHOT_SAMPLE_RATE ?? 1)}
      screenshotUploadTimeoutMs={2000}
      maskSelectors={[
        '[data-allstak-mask]',
        '[data-sensitive]',
        '[data-shreeksakn-sensitive]',
      ]}
      ignoreSelectors={[
        '[data-allstak-ignore]',
        '[data-shreeksakn-visual-ignore]',
      ]}
      enableHttpTracking
      tracesSampleRate={1}
    >
      <AllStakUserBootstrap />
      {children as any}
    </AllStakProvider>
  );
}

function registerAllStakDevVerifier() {
  if (typeof window === 'undefined' || !import.meta.env.DEV) return;
  const verifySearch = new URLSearchParams(window.location.search).has('allstak_verify');
  const target = window as unknown as { __ALLSTAK_VERIFY__?: () => Promise<void> };
  target.__ALLSTAK_VERIFY__ = async () => {
    AllStak.addBreadcrumb('allstak.verify', 'Wizard browser verification triggered', 'info');
    AllStak.setUser({ id: 'allstak-wizard-browser-user', email: 'redacted-allstak-wizard@example.invalid' });
    AllStak.setTag('allstak.verify', 'browser-runtime');
    AllStak.captureMessage('AllStak React browser setup verification', 'info');
    AllStak.captureException(new Error('AllStak React browser setup verification error'));
    await AllStak.flush?.();
  };
  if (verifySearch) {
    window.setTimeout(() => {
      void target.__ALLSTAK_VERIFY__?.();
    }, 500);
  }
}
registerAllStakDevVerifier();
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AllStakRoot>
    <QueryClientProvider client={queryClient}>
      <Root />
    </QueryClientProvider>
    </AllStakRoot>
  </StrictMode>,
);
